<template>
  <div>

    <div v-show="step == 1">

      <!-- Loading Spinner -->
      <div v-if="!availableSlots" class="loading-container">
        <div class="spinner"></div>
        <p class="loading-text">Verfügbare Termine werden geladen...</p>
      </div>

      <!-- Main Content - Only show when data is loaded -->
      <div v-if="availableSlots">
        <div class="select-employee mb-3" v-if="employeeSelect">
          <p class="mb-0 font-bold">{{ employeeSelectText }}</p>
          <Select2 class="mb-2" v-model="selectedEmployeeId" :options="availableEmployees" @change="onSelectedEmployee()" :settings="{ minimumResultsForSearch: 100, placeholder: 'Bitte wählen' }" />
        </div>
        
        <div class="booking-info-box" role="note">
          <svg class="booking-info-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="16" x2="12" y2="12"></line>
            <line x1="12" y1="8" x2="12.01" y2="8"></line>
          </svg>
          <span>Terminbuchungen sind möglich bis <strong>{{ maxBookingDateFormatted }}</strong></span>
        </div>

        <div class="form-group" :class="{ 'trim-cal-last-week': trimCalendarLastWeek }">
          <vc-date-picker v-model="selectedDate" :value="null" :min-date='attributes.min_date' :max-date='attributes.max_date' is-inline is-expanded title-position="left" :available-dates='attributes.available_dates' :disabled-dates='attributes.disabled_dates' :attributes="vcCalendarAttrs" @dayclick='dayClicked' @update:from-page="p => calendarPage = p" ref="datepicker" />
        </div>

        <div v-if="timSlotsloading" class="slot-loading-container">
          <div class="slot-spinner"></div>
        </div>

        <div v-if="availableSlots.slots && availableSlots.slots.length > 0 && timSlotsloading === false">

          <p>Welche Zeit passt Ihnen am <strong>{{ availableSlots.selectedDate | formatDate }}</strong> am besten?</p>

          <div class="row no-gutters">
            <div class="col-3" v-for="slot in availableSlots.slots" :key="slot.id" @click="onSelectSlot(slot)">
              <div class="booking-slot p-2 mb-2 shadow rounded" v-bind:class="{ active: slotActive(slot.id) }">
                <p class="small m-0 text-center">{{ slot.start | formatTimeSlot }}</p>
              </div>
            </div>
          </div>

        </div>

        <div v-if="availableSlots.slots && availableSlots.slots.length == 0 && timSlotsloading === false">
          <div class="alert alert-danger" role="alert">
            {{ $parent.content.setting_no_appointments_message_kl }}
          </div>
        </div>

        <button class="btn btn-primary my-3 mr-1 float-right" v-if="selectedSlotIds.length" @click="onStep2()">Weiter</button>
      </div>

    </div>

    <div v-show="step == 2">

      <div v-if="selectedSlotIds.length" class="mt-1">
        <Insurance @insurance-data-completed="getInsuranceDataCheckout" :initInsuranceData="initInsuranceData" />
      </div>

    </div>

  </div>
</template>

<script>
import Insurance from "./Insurance.vue";

export default {

  name: 'KSRViewingAppointment',
  components: {Insurance},
  data() {
    return {
      studioId: false,
      selectedDate: '',
      availableSlots: false,
      allSlots: [],
      licenseplate: 'k',
      licenseplateFound: false,
      selectedSlots: [],
      selectedSlotIds: [],
      selectedEmployeeId: '',
      insuranceData: false,
      initInsuranceData: 1,
      step: 1,
      timSlotsloading: false,
      availableEmployees: [],
      employeeSelect: false,
      employeeSelectText: '',
      employeeSelectedText: '',
      attributes:
        {
          min_date: new Date(),
          max_date: '',
          disabled_dates: {},
          //disabled_dates: { weekdays: [1] },
          available_dates: [],
        },
      rangeAnchor: null,
      min_date_range: '',
      max_date_range: '',
      calendarPage: null,
      firstBookableSlot: null,
    }
  },
  computed: {
    maxBookingDateFormatted() {
      const days = this.$parent.content.setting_maxdays_viewing || 30;
      const date = moment().add(days, 'days');
      const weekday = date.toDate().toLocaleDateString('de-DE', { weekday: 'long' });
      return `${weekday}, ${date.format('D.M.YYYY')}`;
    },
    trimCalendarLastWeek() {
      const page = this.calendarPage;
      let year, month;
      if (page) {
        year = page.year; month = page.month;
      } else if (this.selectedDate) {
        const d = new Date(this.selectedDate);
        year = d.getFullYear(); month = d.getMonth() + 1;
      } else {
        return false;
      }
      const firstDayMon = (new Date(year, month - 1, 1).getDay() + 6) % 7; // Mon=0
      const daysInMonth = new Date(year, month, 0).getDate();
      return firstDayMon + daysInMonth <= 35;
    },
    vcCalendarAttrs() {
      if (!this.rangeAnchor) return [];

      const rangeStart = new Date(this.rangeAnchor);
      rangeStart.setHours(0, 0, 0, 0);
      const rangeEnd = new Date(this.rangeAnchor);
      rangeEnd.setDate(rangeEnd.getDate() + 30);
      rangeEnd.setHours(23, 59, 59, 999);

      if (this.attributes.max_date) {
        const maxEnd = new Date(this.attributes.max_date);
        maxEnd.setHours(23, 59, 59, 999);
        if (maxEnd < rangeEnd) {
          rangeEnd.setTime(maxEnd.getTime());
        }
      }

      const beforeMin = new Date(this.attributes.min_date);
      beforeMin.setHours(0, 0, 0, 0);

      const availableSet = new Set(
        (this.attributes.available_dates || []).map(d => moment(d).format('YYYY-MM-DD'))
      );
      const unavailableDates = [];
      const cursor = new Date(rangeStart);
      while (cursor <= rangeEnd) {
        if (!availableSet.has(moment(cursor).format('YYYY-MM-DD'))) {
          unavailableDates.push(new Date(cursor));
        }
        cursor.setDate(cursor.getDate() + 1);
      }

      const attrs = [
        {
          key: 'unavailable-in-range',
          highlight: { class: 'vc-unavail-hl', contentClass: 'vc-unavailable' },
          dates: unavailableDates
        },
        {
          key: 'booking-range',
          highlight: {
            class: 'vc-booking-range-hl',
            contentClass: 'vc-booking-range-content',
            backgroundColor: '#e8f0fe',
            borderWidth: '0',
            borderRadius: '0'
          },
          dates: { start: rangeStart, end: rangeEnd }
        },
        {
          key: 'before-min',
          highlight: { class: 'vc-oor-hl', contentClass: 'vc-oor' },
          dates: { start: null, end: beforeMin }
        }
      ];

      if (this.attributes.max_date) {
        const afterMax = new Date(this.attributes.max_date);
        afterMax.setDate(afterMax.getDate() + 1);
        afterMax.setHours(0, 0, 0, 0);
        attrs.push({
          key: 'after-max',
          highlight: { class: 'vc-oor-hl', contentClass: 'vc-oor' },
          dates: { start: afterMax, end: null }
        });
      }

      return attrs;
    }
  },
  created() {

    // get studioId from parent
    this.studioId = this.$parent.studioId;

    // Calculate tomorrow's date first
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    let tomorrowStr = moment(String(tomorrow)).format('YYYY-MM-DD');

    this.getSlots(tomorrowStr);
  },
  methods: {
    getSlots(tomorrowStr, preferredDate) {
      let licenseplate = encodeURIComponent(this.licenseplate);
      let url = '/api/ksr/' + this.studioId + '/' + licenseplate + '/get-slots/?date=' + tomorrowStr;
      if (this.selectedEmployeeId) url += '&employeeId=' + this.selectedEmployeeId;
      if (this.firstBookableSlot) url += '&firstBookableSlot=' + encodeURIComponent(this.firstBookableSlot);
      url += '&identityToken=' + this.identityToken;

      var _this = this;

      axios.get(url)
          .then((response) => {
            //console.log(response);
            if (!response) {
              _this.error = 'Wir haben keinen laufenden Auftrag zu diesem Kennzeichen gefunden.';
              _this.loading = false;
              // scroll down
              setTimeout(function () {
                var element = document.querySelector(".infobox");
                if (element) {
                  element.scrollIntoView();
                }
              }, 200);
            } else {
              _this.licenseplateFound = true;
              if (!_this.firstBookableSlot && response.data.firstBookableSlot) {
                _this.firstBookableSlot = response.data.firstBookableSlot;
              }
              _this.attributes.available_dates = response.data.availableDates;
              _this.rangeAnchor = moment(tomorrowStr).toDate();
              _this.attributes.max_date = new Date();
              _this.attributes.max_date.setDate(
                  _this.attributes.min_date.getDate() + (_this.$parent.content.setting_maxdays_viewing || 30)
              );
              
              // Employee select data
              _this.availableEmployees = response.data.availableEmployees || [];
              if (!_this.selectedEmployeeId) {
                _this.selectedEmployeeId = response.data.initialEmployeeId || '';
              }
              _this.employeeSelect = response.data.employeeSelect || false;
              _this.employeeSelectText = response.data.employeeSelectText || '';
              _this.employeeSelectedText = response.data.employeeSelectedText || '';

              _this.min_date_range = new Date();
              _this.max_date_range = new Date();
              _this.min_date_range.setDate(_this.min_date_range.getDate() + 1); // tomorrow
              _this.max_date_range.setDate(_this.min_date_range.getDate() + Math.min(_this.$parent.content.setting_maxdays_viewing, 30));

              _this.allSlots = response.data.slots || [];
              let dateToShow = (response.data.availableDates && response.data.availableDates[0]) || tomorrowStr;
              if (preferredDate) {
                const prefStr = moment(preferredDate).format('YYYY-MM-DD');
                const found = (response.data.availableDates || []).find(d => moment(d).format('YYYY-MM-DD') === prefStr);
                if (found) {
                  dateToShow = found;
                } else {
                  const maxDate = _this.attributes.max_date;
                  const nextAvail = (response.data.availableDates || []).find(d =>
                    moment(d).isSameOrAfter(moment(preferredDate), 'day') &&
                    (!maxDate || moment(d).isSameOrBefore(moment(maxDate), 'day'))
                  );
                  if (nextAvail) {
                    dateToShow = nextAvail;
                  } else {
                    _this.selectedDate = new Date(preferredDate);
                    _this.availableSlots = { slots: [], selectedDate: preferredDate };
                    _this.timSlotsloading = false;
                    return;
                  }
                }
              }
              _this.showSlotsForDay(dateToShow, response.data);
            }
          })
          .catch(function (error) {
            if (error.response && error.response.status === 404) {
              _this.licenseplateFound = true;
              _this.availableSlots = { slots: [] };
              return;
            }
            console.error('API request failed:', error);
            _this.error = 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.';
            _this.loading = false;
          });
    },
    onStep2() {
      // scroll down
      setTimeout(function() {
        var element = document.querySelector("#booking-container");
        element.scrollIntoView();
      }, 200);
      this.step = 2;
    },
    onSelectedEmployee() {
      const tomorrowStr = moment().add(1, 'days').format('YYYY-MM-DD');
      this.fetchSlotsForDay(tomorrowStr).then(() => {
        this.rangeAnchor = moment(tomorrowStr).toDate();
        const firstDate = this.attributes.available_dates && this.attributes.available_dates[0];
        if (firstDate) {
          const firstStr = moment(firstDate).format('YYYY-MM-DD');
          if (firstStr !== tomorrowStr) {
            this.showSlotsForDay(firstStr);
          }
        }
      });
    },
    dayClicked(day) {
      if (this.$parent.content.setting_maxdays_viewing === 0) {
        return;
      }
      const dateStr = moment(day.id).format('YYYY-MM-DD');
      const rangeStart = moment(this.rangeAnchor);
      const rangeEnd = moment(this.rangeAnchor).add(30, 'days');
      const withinRange = moment(dateStr).isBetween(rangeStart, rangeEnd, 'day', '[]');

      if (!withinRange) {
        this.timSlotsloading = true;
        this.rangeAnchor = moment(dateStr).toDate();
        // Always pass the clicked day as preferredDate so getSlots never
        // auto-jumps to a different available day than what the user selected.
        this.getSlots(dateStr, dateStr);
        return;
      }

      if (this.attributes.available_dates && this.attributes.available_dates.length > 0) {
        if (!this.attributes.available_dates.includes(dateStr)) {
          // Day has no free slots — show the empty-state message and stay
          // on the clicked day instead of jumping to the next available one.
          this.showSlotsForDay(dateStr);
          return;
        }
      }

      const cachedForDay = (this.allSlots || []).filter(s => moment(s.start).format('YYYY-MM-DD') === dateStr);
      if (cachedForDay.length > 0) {
        this.showSlotsForDay(dateStr);
      } else {
        this.fetchSlotsForDay(dateStr);
      }
    },
    showSlotsForDay(dateStr, baseData) {
      const slotsForDay = (this.allSlots || []).filter(s => moment(s.start).format('YYYY-MM-DD') === dateStr);
      this.availableSlots = Object.assign({}, baseData || this.availableSlots || {}, {
        slots: slotsForDay,
        selectedDate: dateStr
      });
      this.selectedDate = new Date(dateStr);
      this.timSlotsloading = false;
      const cal = this.$refs.datepicker && this.$refs.datepicker.$refs.calendar;
      if (cal) cal.move(new Date(dateStr));
    },
    fetchSlotsForDay(dateStr) {
      this.timSlotsloading = true;
      var _this = this;
      let url = '/api/ksr/' + this.studioId + '/' + encodeURIComponent(this.licenseplate) + '/get-slots/?date=' + dateStr;
      if (this.selectedEmployeeId) {
        url += '&employeeId=' + this.selectedEmployeeId;
      }
      if (this.jobId) {
        url += '&jobId=' + this.jobId;
      }
      if (this.firstBookableSlot) url += '&firstBookableSlot=' + encodeURIComponent(this.firstBookableSlot);
      url += '&identityToken=' + this.identityToken;

      return axios.get(url)
          .then((response) => {
            if (response.data.availableDates) {
              _this.attributes.available_dates = response.data.availableDates;
            }
            if (response.data.slots) {
              _this.allSlots = response.data.slots;
            }
            const targetDay = moment(response.data.selectedDate || dateStr).format('YYYY-MM-DD');
            _this.showSlotsForDay(targetDay, response.data);
          })
          .catch(function (error) {
            if (error.response && error.response.status === 404) {
              _this.availableSlots = { slots: [], selectedDate: dateStr };
              _this.selectedDate = new Date(dateStr);
            }
            _this.timSlotsloading = false;
          });
    },
    slotActive(slot_id) {
      if(this.selectedSlotIds.indexOf(slot_id) > -1)
        return true;
      else
        return false;
    },
    onSelectSlot(slot) {
      //console.log(slot);
      this.selectedSlotIds = []
      this.selectedSlots = [];
      this.selectedSlotIds.push(slot.id);
      this.selectedSlots.push(slot);

      //if(this.employeeSelect == 0) {
      this.selectedEmployeeId = slot.availableEmployees[0];
      //}

      /*const index = this.selectedSlotIds.indexOf(slot.id);
      if(index === -1) {
          this.selectedSlotIds.push(slot.id);
          this.selectedSlots.push(slot);
          console.log("1");
      }
      else { // remove
          this.selectedSlotIds.splice(index, 1);
          console.log(slot.id);
          for (let index = 0; index < this.selectedSlots.length; ++index) {
              if(this.selectedSlots[index].id == slot.id) {
                  this.selectedSlots.splice(index, 1);
              }
          }
      }*/
    },
    onSlotsSelectedGuest() {
      // Update 2025-07-27: Guest Booking (shows Insurance and Guest options after Viewing Appointment)
      this.rootSection = 'guestBookingViewingAppointment';
      setTimeout(function() {
        var element = document.querySelector("#booking-container");
        element.scrollIntoView();
      }, 200);
    },
    goBack() {
      // Go back using hash navigation (since router might not be available)
      window.location.hash = '';
      // Alternative: this.$router.go(-1) if router is available
    },
    getInsuranceDataCheckout(value) {

      console.log(value);
      //console.log(value.damagetype);
      this.insuranceData = value;

      // save in local storage
      localStorage.setItem('selectedSlotStart', this.selectedSlots[0]?.start || '');
      localStorage.setItem('selectedEmployeeId', this.selectedEmployeeId || '');
      localStorage.setItem('licensePlate', this.licenseplate || '');
      localStorage.setItem('typeDetails', JSON.stringify(this.typeDetails || {}));
      localStorage.setItem('insuranceData', JSON.stringify(this.insuranceData || {}));

      this.rootSection = '';
      if(this.jobId) {
        this.$router.push('/viewing-appointment-checkout/' + this.jobId);
      }
      else {
        this.$router.push('/viewing-appointment-checkout/create');
      }
      /*this.$router.push({
        path: '/viewing-appointment-checkout',
        query: { jobId: this.jobId }
      });*/
    },
    hasNotPast() {
      /*let currentDayMom = moment(this.formdata.currentDay);
      let prevDayMom = currentDayMom.subtract(6, 'days').set({"hour": 0, "minute": 0});
      let today = moment().set({"hour": 0, "minute": 0});
      return prevDayMom >= today;*/
      return true;
    },
    formatDateYear(dateString) {
      let dateObj = new Date(dateString);
      let momentObj = moment(dateObj);
      return momentObj.format('D.M.YYYY');
    },
    onNextPeriod() {
      let new_start = new Date(this.max_date_range.getTime() + 86400000);
      this.getSlots(moment(new_start).format('YYYY-MM-DD'));
    },
    onPastPeriod() {
      
    }
  }
}
</script>

<style scoped>
/* v-calendar v1 renders a flat 7×7 CSS grid: 7 weekday headers + 42 day cells.
   Items 43–49 are the 6th week row. Hide it when the month only needs 5 rows.
   >>> is required to pierce the scoped CSS boundary into v-calendar's internals. */
.trim-cal-last-week >>> .vc-weeks > *:nth-child(n+43) {
  display: none !important;
}

.booking-info-box {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #f0f7ff 0%, #e6f2ff 100%);
  border-left: 3px solid #3b82f6;
  border-radius: 6px;
  color: #1e3a5f;
  font-size: 0.9rem;
  line-height: 1.4;
}

.booking-info-icon {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  color: #3b82f6;
}

.booking-info-box strong {
  color: #1e3a5f;
  font-weight: 600;
}

/* Flatten the start/end cap circles of the booking range into the strip */
::v-deep .vc-booking-range-hl {
  background-color: #e8f0fe !important;
  border-radius: 0 !important;
  border-width: 0 !important;
  border-color: transparent !important;
}

/* Keep the day number readable on the start/end caps (v-calendar defaults to white) */
::v-deep .vc-booking-range-content {
  color: inherit !important;
}

/* Hide the highlight background for out-of-range dates */
::v-deep .vc-oor-hl {
  background-color: transparent !important;
  border-color: transparent !important;
  border-width: 0 !important;
}

/* Fade and disable out-of-range day numbers.
   color: inherit defeats v-calendar's default `vc-text-white` on the start cap
   of any range — without it, the first after-max day (and the last before-min day)
   render as white-on-white and disappear. Same neutralizer as .vc-booking-range-content. */
::v-deep .vc-oor {
  opacity: 0.2 !important;
  color: inherit !important;
  cursor: default !important;
  pointer-events: none !important;
}

/* v-calendar's default theme sets opacity:0 on the day wrapper for prev/next-month
   preview cells, which would collapse our .vc-oor fade to fully invisible. Re-show
   the wrapper so the .vc-oor opacity above governs out-of-range cells uniformly. */
::v-deep .vc-day:has(.vc-oor) > .vc-h-full {
  opacity: 1 !important;
}

/* Hide the highlight used only to attach vc-unavailable class */
::v-deep .vc-unavail-hl {
  background-color: transparent !important;
  border-color: transparent !important;
  border-width: 0 !important;
}

/* Same color: inherit neutralizer as .vc-oor / .vc-booking-range-content. Each entry in
   the unavailable-in-range date array is rendered with v-calendar's solid fillMode, which
   sets `vc-text-white` on the day content — invisible on the page and on the selected-day
   circle. Without this rule a selected unavailable day renders as a blue dot with no number. */
::v-deep .vc-unavailable {
  color: inherit !important;
}

/* Target the full day cell container via :has() */
::v-deep .vc-day:has(.vc-unavailable),
::v-deep .vc-day:has(.vc-oor) {
  cursor: default !important;
  pointer-events: none !important;
}

.slot-loading-container {
  display: flex;
  justify-content: center;
  padding: 1rem 0;
}

.slot-spinner {
  width: 24px;
  height: 24px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  min-height: 200px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  color: #6c757d;
  font-size: 1rem;
  margin: 0;
  text-align: center;
}
</style>